import axios, { AxiosResponse } from 'axios';
import { config } from '../../config';
import useAuthStore from '../../store/authStore';

// Create API instance with base configuration
const api = axios.create({
  baseURL: config.apiUrl || 'https://prestige-ai-api.onrender.com',
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth-token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Log request for debugging
  console.log('API Request:', {
    url: config.url,
    method: config.method,
    baseURL: config.baseURL
  });
  
  return config;
}, (error) => {
  console.error('Request interceptor error:', error);
  return Promise.reject(error);
});

// Response interceptor for better error handling
api.interceptors.response.use(
  async (response: AxiosResponse) => {
    // For blob responses (Excel files), return the raw data
    if (response.config.responseType === 'blob') {
      if (response.data instanceof Blob && response.data.type === 'application/json') {
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.message || 'Failed to download report');
      }
      return response.data;
    }

    // Return the response data directly without additional wrapping
    return response.data;
  },
  (error) => {
    // Log detailed error information
    console.error('API Error:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      url: error.config?.url,
      method: error.config?.method,
      data: error.response?.data
    });

    if (error.response?.status === 401 || error.response?.status === 403) {
      // Clear auth state on 401/403
      localStorage.removeItem('auth-token');
      useAuthStore.getState().logout();

      // Redirect to login if not already there
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error.response?.data?.message || error.message);
  }
);

export default api;